import { Link, useParams } from 'react-router-dom'
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  MoreVertical,
  Trash2,
  Clock,
  Settings,
  Radio,
  Loader2,
} from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import type { AppWithStats } from '../types'

interface AppCardProps {
  app: AppWithStats
  onDelete: (id: string) => Promise<void>
  onSettings: (app: AppWithStats) => void
  onPing?: (id: string) => Promise<void>
}

export default function AppCard({ app, onDelete, onSettings, onPing }: AppCardProps) {
  const { teamId } = useParams<{ teamId: string }>()
  const [menuOpen, setMenuOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isPinging, setIsPinging] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (confirm('Are you sure you want to delete this app? All data will be lost.')) {
      setIsDeleting(true)
      try {
        await onDelete(app.id)
      } finally {
        setIsDeleting(false)
      }
    }
    setMenuOpen(false)
  }

  const handlePing = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!onPing || !app.health_url) return
    setIsPinging(true)
    setMenuOpen(false)
    try {
      await onPing(app.id)
    } finally {
      setIsPinging(false)
    }
  }

  // Derive ping status from last_ping object
  const lastPingStatus = app.last_ping?.is_success ?? null

  const getStatusIcon = () => {
    if (lastPingStatus === null) {
      return <Clock className="w-5 h-5 text-slate-500" />
    }
    if (lastPingStatus === true) {
      return <CheckCircle className="w-5 h-5 text-green-600" />
    }
    return <XCircle className="w-5 h-5 text-red-600" />
  }

  const getStatusColor = () => {
    if (app.unresolved_errors > 0 || lastPingStatus === false) {
      return 'border-red-500/50'
    }
    if (app.error_count_24h > 0) {
      return 'border-yellow-500/50'
    }
    return 'border-slate-200'
  }

  return (
    <Link
      to={`/t/${teamId}/apps/${app.id}`}
      className={`block bg-slate-50/50 border ${getStatusColor()} rounded-xl p-6 hover:border-primary-600/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary-600/10 relative`}
    >
      {/* Menu button */}
      <div className="absolute top-4 right-4" ref={menuRef}>
        <button
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            setMenuOpen(!menuOpen)
          }}
          className="p-1 hover:bg-slate-200 rounded transition-colors"
        >
          <MoreVertical className="w-5 h-5 text-slate-400" />
        </button>
        {menuOpen && (
          <div className="absolute right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-10 min-w-[120px]">
            {onPing && app.health_url && (
              <button
                onClick={handlePing}
                disabled={isPinging}
                className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2 disabled:opacity-50"
              >
                {isPinging ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Radio className="w-4 h-4" />
                )}
                {isPinging ? 'Pinging...' : 'Ping Now'}
              </button>
            )}
            <button
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                setMenuOpen(false)
                onSettings(app)
              }}
              className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Settings
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-slate-100 flex items-center gap-2 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        )}
      </div>

      {/* App name and status */}
      <div className="flex items-start gap-3 mb-4">
        {getStatusIcon()}
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-slate-900 truncate">{app.name}</h3>
          {app.description && <p className="text-sm text-slate-600 truncate">{app.description}</p>}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mt-4">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide">Errors 24h</p>
          <p
            className={`text-lg font-semibold ${app.error_count_24h > 0 ? 'text-red-600' : 'text-slate-900'}`}
          >
            {app.error_count_24h}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide">Unresolved</p>
          <p
            className={`text-lg font-semibold ${app.unresolved_errors > 0 ? 'text-yellow-600' : 'text-slate-900'}`}
          >
            {app.unresolved_errors}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide">Uptime</p>
          <p className="text-lg font-semibold text-slate-900">
            {app.uptime_24h !== null ? `${app.uptime_24h.toFixed(1)}%` : '-'}
          </p>
        </div>
      </div>

      {/* Issues indicator */}
      {(app.unresolved_errors > 0 || lastPingStatus === false) && (
        <div className="mt-4 flex items-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          <span className="text-red-600">
            {lastPingStatus === false
              ? 'App is down'
              : `${app.unresolved_errors} unresolved errors`}
          </span>
        </div>
      )}
    </Link>
  )
}
